import type { Components } from 'react-markdown';
import { createChatComponents } from './chat-components';
import { createProseComponents } from './prose-components';
import type { MarkdownSize } from './sizes';

/**
 * The markdown component maps, built once per size and reused forever.
 *
 * Identity is the contract here, not just an optimisation. react-markdown
 * treats the entries in this map as element *types*, so a structurally
 * identical but freshly allocated map remounts every block it produced. For a
 * streamed answer, where `content` changes on every token, that throws away a
 * mermaid diagram's render and the reader's view toggle many times a second.
 *
 * The maps close over nothing but their size, so sharing one instance across
 * every `MarkdownContent` on the page is safe. Anything render-dependent must
 * be resolved inside a component, not captured when the map is built.
 */
const COMPONENT_MAPS = new Map<MarkdownSize, Components>();

export function buildComponents(size: MarkdownSize): Components {
  const cached = COMPONENT_MAPS.get(size);
  if (cached) return cached;
  const built = size === 'prose' ? createProseComponents() : createChatComponents(size);
  COMPONENT_MAPS.set(size, built);
  return built;
}
